import { html, TemplateResult, PropertyValues } from 'lit';
import { property, state, query, customElement } from 'lit/decorators.js';
import { styles } from '../view/styles';
import { enUS } from 'date-fns/locale';
import { DatePickerCalendarWebComponent } from '../../gzDateRangePicker';
import { getMonth, getYear, format, getTime } from 'date-fns';
import type { PickerStrings } from '../types';
import { FormFieldInput } from '../../gzForm/formField/component/FormFieldInput';
import { CalendarEvents } from '../../gzDateRangePicker/types';
import { WEB_COMPONENT_GZ_DROPDOWN_MENU } from '../../../webcomponents.constants';
import { GzDropdownMenuWebComponent } from '../../gzDropdownMenu';
import { LabelWidthValues, MenuPosition } from '../../../types/common/types';
import { getLocaleObjectFromUserLocale } from 'apps/webcomponents/src/utils/i18n.utils';
import { translateFn } from '../../../utils/translator.helper';
import { GzTimePickerWebComponent } from '../../gzTimePicker';
import { utcToZonedTime, zonedTimeToUtc, format as formatInTimezone } from 'date-fns-tz';

@customElement('gz-date-picker')
export class GzDatePickerWebComponent extends FormFieldInput<number> {
  @property({ type: String, reflect: true })
  placeholder = '';

  /**
   * Set locale of the calendar. The default is 'en-US'
   */
  @property({ type: String, reflect: true })
  userLocale = '';

  /**
   * Sets the timezone of the calendar, in which the selected date and time will be rendered in the label.
   * The default is 'UTC'
   */
  @property({ type: String, reflect: true })
  timezone = 'UTC';

  /**
   * Max date allowed for selection. Format is Unix timestamp.
   * When max is set, the dates after the maximum timestamp will be disabled on the calendar.
   */
  @property({ type: Number })
  max;

  /**
   * Minimal date allowed for selection. Format is Unix timestamp.
   * When min is set, the dates before the minimum timestamp will be disabled on the calendar.
   */
  @property({ type: Number })
  min;

  /**
   * Set month.
   * Default is current month.
   */
  @property({ type: Number })
  month;

  /**
   * Set year.
   * Default is current year.
   */
  @property({ type: Number })
  year;

  @property({ type: Number, reflect: true })
  selectedTimestamp;

  @property({ type: String, reflect: true })
  labelWidth = LabelWidthValues.LARGE;

  /**
   * If set to TRUE, the picker will display a time picker component and CONFIRM/CANCEL button in the dropdown. The
   * selected timestamp value will change only when the user presses the CONFIRM button.
   */
  @property({ type: Boolean, reflect: true })
  hasTimePicker = false;

  @property({ type: Object })
  pickerStrings: PickerStrings;

  /**
   * This will be used to display the formatted selected date
   */
  @state()
  selectedDateString = '';

  @state()
  private locale: Locale = enUS;

  /**
   * This will be used to store the initial timestamp when the dropdown is opened.
   * If the user cancels the selection, the component will revert to this value.
   * @private
   */
  @state()
  private initialTimestamp: number | undefined;

  /**
   * This will be used to store the selected date when the user selects a date from the calendar, for easier timestamp manipulation.
   * @private
   */
  @state()
  private tempSelectedDate: number | undefined;

  @query('#datePicker')
  datePicker!: DatePickerCalendarWebComponent;

  @query('#timePicker')
  timePicker!: GzTimePickerWebComponent;

  @property({ type: String, attribute: true, reflect: true })
  menuPosition: MenuPosition;

  @query(WEB_COMPONENT_GZ_DROPDOWN_MENU)
  private readonly dropdownMenu!: GzDropdownMenuWebComponent;

  static get styles() {
    return styles;
  }

  constructor() {
    super();
    const now = new Date();
    this.month = getMonth(now) + 1;
    this.year = getYear(now);
  }

  private storeInitialTimestamp(): void {
    this.initialTimestamp = this.selectedTimestamp;
  }

  private setSelectedTimestamp(timestamp: number): void {
    this.selectedTimestamp = timestamp;
  }

  private setSelectedDateString(selectedTimestamp?: number): void {
    this.selectedDateString = selectedTimestamp ? this.getFormattedSelectedDate(selectedTimestamp) : '';
  }

  updated(properties: PropertyValues): void {
    let shouldUpdateSelectedDateString = false;

    if (properties.has('userLocale')) {
      this.locale = getLocaleObjectFromUserLocale(this.userLocale);
      shouldUpdateSelectedDateString = true;
    }

    if (properties.has('selectedTimestamp') || properties.has('timezone')) {
      shouldUpdateSelectedDateString = true;
    }

    if (shouldUpdateSelectedDateString) {
      this.setSelectedDateString(this.selectedTimestamp);
    }
  }

  render() {
    return html`
      <gz-dropdown-menu
        class="${this.invalid ? 'invalid' : ''}"
        selectedValuesStrings="${this.selectedDateString}"
        .labelWidth="${this.labelWidth}"
        placeholder="${this.placeholder}"
        icon="ds-icon ds-icon-calendar"
        .menuPosition="${this.menuPosition}"
        @open="${this.handleDropdownOpen}"
      >
        <div class="dropdown-menu-wrapper">
          <div class="content-wrapper">
            ${this.renderTimePicker()} ${this.renderCalendar()} ${this.renderCalendarActionButtons()}
          </div>
        </div>
      </gz-dropdown-menu>
    `;
  }

  public setFormValue(value: number): void {
    this.setSelectedTimestamp(value);
  }

  public getFormValue(): number {
    return this.selectedTimestamp;
  }

  renderCalendar() {
    return html`
      <div class="calendar-wrapper">
        <gz-datepicker-calendar
          id="datePicker"
          min="${this.min}"
          max="${this.max}"
          noRange
          prev
          next
          .dateFrom="${this.selectedTimestamp}"
          .locale="${this.locale}"
          .timezone="${this.timezone}"
          month="${this.month}"
          year="${this.year}"
          @dateFromChanged="${this.handleDateSelected}"
        >
          <gz-button
            slot="calendar-header"
            @click="${this.navigateToCurrentMonth}"
            type="quiet"
            color="secondary"
            buttonHeight="small"
            buttonFontSize="small"
            class="navigate-current"
          >
            ${this.pickerStrings?.currentDay ?? ''}
          </gz-button>
        </gz-datepicker-calendar>
      </div>
    `;
  }

  private handlePrevMonth(event: Event): void {
    (event.target as HTMLElement).blur();
    this.datePicker.handlePrevMonth();
  }

  private handleNextMonth(event: Event): void {
    (event.target as HTMLElement).blur();
    this.datePicker.handleNextMonth();
  }

  private renderTimePicker(): TemplateResult | undefined {
    if (this.hasTimePicker) {
      return html`
        <div class="time-picker-wrapper">
          <span class="label">${translateFn('Time')}</span>
          <gz-time-picker id="timePicker"></gz-time-picker>
        </div>
      `;
    }
  }

  private renderCalendarActionButtons(): TemplateResult | undefined {
    if (!this.hasTimePicker) {
      return;
    }

    const { cancelActionButton, confirmActionButton } = this.pickerStrings;

    return html`
      <div class="calendar-actions">
        <gz-button
          buttonFontSize="small"
          class="footer-item"
          color="primary"
          buttonHeight="small"
          type="quiet"
          ?disabled="${this.invalid || !this.tempSelectedDate}"
          @click="${this.handleDateConfirm}"
          >${confirmActionButton}</gz-button
        >
        <gz-button
          buttonFontSize="small"
          class="footer-item"
          color="secondary"
          buttonHeight="small"
          type="quiet"
          @click="${this.handleDateCancel}"
          >${cancelActionButton}</gz-button
        >
      </div>
    `;
  }

  private setTimePickerValues(hours: number, minutes: number): void {
    this.timePicker.setFormValue({ hours: hours, minutes });
  }

  private handleDropdownOpen(): void {
    this.storeInitialTimestamp();

    if (!this.hasTimePicker || !this.timePicker) {
      return;
    }

    const timeZone = this.timezone;

    if (this.selectedTimestamp) {
      // Convert the selected timestamp from UTC to the timezone of the calendar
      const date = new Date(this.selectedTimestamp * 1000);
      const zonedDate = utcToZonedTime(date, timeZone);
      const hours = zonedDate.getHours();
      const minutes = zonedDate.getMinutes();
      this.setTimePickerValues(hours, minutes);
      this.tempSelectedDate = this.selectedTimestamp;
    } else {
      this.setTimePickerValues(0, 0);
      this.tempSelectedDate = undefined;
    }
  }

  private handleDateCancel(): void {
    if (this.initialTimestamp !== undefined) {
      this.setSelectedTimestamp(this.initialTimestamp);
    }
    this.tempSelectedDate = undefined;
    this.dropdownMenu.closeDropdownMenu();
  }

  private handleDateConfirm(): void {
    if (this.hasTimePicker && this.timePicker) {
      this.confirmTimePickerSelection(this.tempSelectedDate);
    }

    this.dispatchEvent(
      new CustomEvent(CalendarEvents.DATE_IS_SELECTED, {
        detail: { date: this.selectedTimestamp },
      })
    );
    this.dropdownMenu.closeDropdownMenu();
  }

  private confirmTimePickerSelection(tempSelectedDate: number | undefined): void {
    if (!tempSelectedDate) {
      return;
    }

    const selectedTime = this.timePicker.getFormValue();
    const timeZone = this.timezone;

    const date = new Date(tempSelectedDate * 1000);
    date.setHours(selectedTime.hours, selectedTime.minutes, 0, 0);

    const utcDate = zonedTimeToUtc(date, timeZone);
    const finalTimestamp = Math.floor(utcDate.getTime() / 1000);
    this.setSelectedTimestamp(finalTimestamp);
  }

  private handleDateSelected(e: CustomEvent): void {
    const { value, isCurrent } = e.detail;
    this.tempSelectedDate = value;

    if (this.hasTimePicker && this.timePicker) {
      if (isCurrent) {
        const timeOfDay = getTime(utcToZonedTime(Date.now(), this.timezone));
        const hours = parseInt(format(timeOfDay, 'H'), 10);
        const minutes = parseInt(format(timeOfDay, 'm'), 10);
        this.setTimePickerValues(hours, minutes);
      } else {
        this.setTimePickerValues(0, 0);
      }
    } else {
      this.setSelectedTimestamp(value);

      this.dispatchEvent(
        new CustomEvent(CalendarEvents.DATE_IS_SELECTED, {
          detail: { date: this.selectedTimestamp },
        })
      );
      this.dropdownMenu.closeDropdownMenu();
    }
  }

  private getFormattedSelectedDate(timestamp: number): string {
    const date = new Date(timestamp * 1000);
    const timeZonedDate = formatInTimezone(utcToZonedTime(date, this.timezone), 'd MMM yyyy HH:mm', {
      timeZone: this.timezone,
      locale: this.locale,
    });

    return this.hasTimePicker ? timeZonedDate : format(date, 'd MMM yyyy', { locale: this.locale });
  }

  private navigateToCurrentMonth(): void {
    const now = new Date();
    const currentMonth = getMonth(now) + 1;
    const month = currentMonth < 10 ? '0' + currentMonth.toString() : currentMonth.toString();
    const year = getYear(now).toString();

    this.datePicker.navigateToMonthAndYear(month, year);
  }
}
